import { ref, computed } from 'vue';
import { defineStore } from "pinia";
import { useRouter } from 'vue-router';
import { useAsyncState } from '@vueuse/core';
import { useErrorOverlay } from './overlays/error-overlay';
import { useWarningOverlay } from './overlays/warning-overlay';
import { useUserStore } from './user';
import { useStation } from './station';
import axios from "axios";

export const useChargeManagement = defineStore("chargeManagement", () => {
  const user = useUserStore();
  const router = useRouter();
  const station = useStation();
  const error_overlay = useErrorOverlay();
  const warning_overlay = useWarningOverlay();

  /** State **/
  const chargestatus_overlay_visible = ref<boolean>(true);
  const warning_interval_id = ref<ReturnType<typeof setTimeout> | null>(null);

  /** Getters **/
  const is_overlay_visible = computed(() => chargestatus_overlay_visible.value);

  /** Actions **/
  function set_warning_interval(): void {
    const delay: number = +import.meta.env.VITE_APP_DELAY_WARNING_OVERLAY;
    warning_interval_id.value = setInterval(warning_overlay.show, delay);
  }
  function clear_warning_interval(): void {
    if (warning_interval_id.value !== null) {
      clearInterval(warning_interval_id.value);
      warning_interval_id.value = null;
    }
    warning_overlay.hide();
  }
  function show_overlay() { chargestatus_overlay_visible.value = true; }
  function hide_overlay() { chargestatus_overlay_visible.value = false; }
  async function do_start_charge() {
    try {
      const qrcode = station.qrcode;
      /* const response =*/ await axios.post(`/v2/chargecontroller/${qrcode}/start_charge/`);
      // const _ = response.data;
      console.log("[charge-mng.start_charge]: charge started");
      show_overlay();
      if (user.is_guest) {
        set_warning_interval();
      }
      return true;
    } catch (err: any) {
      console.log("[charge-mng.start_charge]: error: ", err);
      switch (err.status) {
        case 408:
          console.log("[charge-mng.start_charge]: start charge timeout");
          error_overlay.show_timeout();
          break;
        case 401:
          router.push("/login");
          break;
        case 403:
          if (err.customMessage === "Authentication credentials were not provided.") {
            console.warn(
              "[charge-mng.start_charge]: CSRF token error. Logging out twice to clear cookies..."
            );
            await user.logout();
            await station.do_fetch(station.qrcode, true);
          }
          break;
        default:
          error_overlay.show(err.customMessage, err.status);
          break;
      }
      return false;
    }
  }
  function start_charge() {
    const qrcode = station.qrcode;
    console.log(`[charge-mng.start_charge]: starting charge on qrcode ${qrcode}`);
    const { state, isLoading } = useAsyncState(do_start_charge(), false);
    return { state, loading: isLoading };
  }
  async function do_stop_charge(): Promise<boolean> {
    try {
      const qrcode = station.qrcode;
      await axios.post(`/v2/chargecontroller/${qrcode}/stop_charge/`);
      console.log("[charge-mng.stop]: charge stopped");
      return true;
    } catch (err: any) {
        switch (err.status) {
          case 408:
            console.log("[charge-mng.stop]: stop charge timeout");
            error_overlay.show_timeout();
            break;
          case 401:
            console.log("[charge-mng.stop]: error 401");
            router.replace("/login");
            break;
          case 403:
            if (err.customMessage === "Authentication credentials were not provided.") {
              console.warn(
                "[charge-mng.stop]: CSRF token error. Logging out twice to clear cookies..."
              );
              await user.logout();
              await station.do_fetch(station.qrcode, true);
            }
            break;
          default:
            console.log(`[charge-mng.stop]: generic error: ${err.status}`)
            console.log(err)
            error_overlay.show(err.customMessage, err.status);
            break;
        }
        return false;
    }
  }
  function stop_charge() {
    const qrcode = station.qrcode;
    console.log(`[charge-mng.stop_charge]: stopping charge on qrcode ${qrcode}`);
    const { state, isLoading } = useAsyncState(do_stop_charge(), false);
    return { state, loading: isLoading };
  }

  return {
    is_overlay_visible,
    show_overlay, hide_overlay,
    do_start_charge, start_charge, do_stop_charge, stop_charge, set_warning_interval, clear_warning_interval
  }
});
